import type { ListMethod } from './list-draft.model';

/** Logical step keys, independent of the display labels. */
export type StepKey = 'details' | 'file' | 'import' | 'select' | 'confirm';

/** Maps a step label to its logical key for navigation. */
export const STEP_LABEL_TO_KEY: Record<string, StepKey> = {
  Details: 'details',
  'Import file': 'file',
  'Import from list': 'import',
  'Select profiles': 'select',
  Confirm: 'confirm',
};

/**
 * Build the dynamic step labels from the selected methods. Pure; moved
 * verbatim from the AddList component so the wizard shape can be unit-tested.
 * The `empty` method is exclusive: it collapses the wizard to a single step.
 */
export function buildSteps(methods: ReadonlySet<ListMethod> | ListMethod[]): string[] {
  const has = (m: ListMethod) =>
    Array.isArray(methods) ? methods.includes(m) : methods.has(m);

  if (has('empty')) return ['Details'];

  const steps = ['Details'];
  if (has('file')) steps.push('Import file');
  if (has('import')) steps.push('Import from list');
  if (has('select')) steps.push('Select profiles');
  if (steps.length > 1) steps.push('Confirm');
  return steps;
}

/** The ordered logical keys for the given methods. */
export function buildStepKeys(methods: ReadonlySet<ListMethod> | ListMethod[]): StepKey[] {
  return buildSteps(methods).map((label) => STEP_LABEL_TO_KEY[label]);
}

export type WizardStepState = 'done' | 'current' | 'upcoming';

export interface WizardStep {
  key: StepKey;
  label: string;
  state: WizardStepState;
}

/**
 * The stepper's steps (board E): the ones the ticked options run (Details,
 * then an import, a list copy or a selection as ticked, then Confirm), each
 * done, current or still to come.
 */
export function wizardSteps(
  methods: ReadonlySet<ListMethod> | ListMethod[],
  currentIndex: number,
): WizardStep[] {
  return buildSteps(methods).map((label, index) => ({
    key: STEP_LABEL_TO_KEY[label],
    label,
    state: index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'upcoming',
  }));
}
